<template>
  <div class="q-pa-md q-mx-md study-table">
    <q-table @row-dblclick="onRowDblClick" @request="onRequest" title="" :rows="rows" :columns="columns" :data="rows"
      :loading="loading" row-key="studyUID" virtual-scroll v-model:pagination="pagination" v-model:fullscreen="fullscreen"
      table-header-class="blue" table-class="blue" card-class="light-blue-9" :rows-per-page-options="[0]" :filter="filter"
      binary-state-sort class="study-table">
      <!-- Adds every column name to the header -->
      <template v-slot:header="props">
        <q-tr :props="props">
          <q-th style="align-items: center; padding-right: 30px" class="row">
            <span class="material-icons search-icon col" style="padding-bottom: 8px; padding-right: 6px">
              search
            </span>
            <span class="col" style="font-size: 14px; padding-left: 0px">Search By</span>
          </q-th>
          <q-th v-for="col in props.cols" :key="col.name" :props="props">
            <!-- Filter input fields-->
            <input v-if="
              col.name != 'operations' &&
              col.name != 'studyDate' &&
              col.name != 'imageCount'
            " class="search-input text-white" :data-id="col.name" v-model="filter[col.name]" dense
              :placeholder="col.label" />

            <div v-if="col.name == 'studyDate'">
              <select v-model="filter.studyDate">
                <option v-for="option in dateDropdownOptions" :key="option.value" :value="option.value">
                  {{ option.text }}
                </option>
              </select>
            </div>

            <div v-if="col.name == 'imageCount'">
              <span class="material-icons" style="font-size: 22px">
                collections
              </span>
            </div>

            <q-btn v-if="col.name == 'operations'" id="clear-filter" @click="resetFilters(props)" icon-right="clear"
              label="Clear Filters" stack>
            </q-btn>

            <!-- Normal headers-->
            <q-div class="nosort-header" v-if="col.name == 'operations'">{{
              col.label
            }}</q-div>
          </q-th>
        </q-tr>
      </template>

      <template v-slot:body="props">
        <q-tr :class="
          props.rowIndex % 2 === 0
            ? 'even-row study-row'
            : 'odd-row study-row'
        " :props="props" @click="props.expand = !props.expand">
          <q-td auto-width>
            <q-btn size="sm" :icon="props.expand ? 'expand_more' : 'chevron_right'" />
          </q-td>

          <!-- Adds every column's value to that column -->
          <q-td v-for="col in props.cols" :key="col.name" :props="props">
            {{ col.value }}
            <!-- Adds buttons if we are in the operations column -->
            <!-- <q-btn
              v-if="col.name === 'operations'"
              class="view-button-study"
              icon-right="collections"
              no-caps
              flat
              dense
              @click="view(props)"
            /> -->
          </q-td>
        </q-tr>
        <!-- Sets the expand behaviour -->
        <q-tr class="expanded-tr" v-show="props.expand" :props="props">
          <q-td colspan="1" style="visibility: none"></q-td>
          <q-td colspan="6" class="info-border">
            <div class="series-info row">
              <div class="open-buttons col-1">
                <q-btn class="open-in-viewer" title="Open Study" @click="viewV3(props)">
                  <span>Open</span><q-icon name="arrow_right" />
                </q-btn>
              </div>

              <div class="col-11">
                <div class="q-pa-md">
                  <q-table title="" class="series-table" :rows="props.row.series" :columns="seriesColumns" row-key="name"
                    hide-bottom v-model:pagination="pagination">
                    <!-- Adds every column name to the header -->
                    <template v-slot:header="props">
                      <q-tr :props="props">
                        <q-th v-for="col in props.cols" :key="col.name" :props="props">
                          {{ col.label }}
                        </q-th>
                      </q-tr>
                    </template>
                  </q-table>
                </div>
              </div>
            </div>
          </q-td>
        </q-tr>
      </template>

      <template v-slot:loading>
        <q-inner-loading showing color="white">
          <q-spinner-hourglass size="lg" />
        </q-inner-loading>
      </template>
    </q-table>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { date } from "quasar";
import axios from "axios";
import Fetcher from "../utils/Fetcher";

// Note: It appears .NET Framework returns upper-starting key values,
// whereas Core returns lower-starting.
// e.g. PatientID (Framework) vs patientID (Core)
const columns = [
  {
    name: "patientName",
    align: "left",
    label: "Patient Name",
    field: "patientName",
    sortable: false,
    style: "width: 300px",
  },
  {
    name: "patientID",
    required: true,
    label: "Patient ID",
    align: "left",
    field: (row) => row.patientID,
    format: (val) => `${val}`,
    sortable: false,
    style: "width: 160px",
  },

  {
    name: "studyDate",
    label: "Study Date",
    field: "studyDate",
    align: "left",
    sortable: false,
    style: "width: 250px",
    format: (val) => {
      var day = val.substring(6, 8);
      var month = val.substring(4, 6);
      var year = val.substring(0, 4);
      return `${day}/${month}/${year}`;
    },
  },
  {
    name: "studyDescription",
    label: "Study Description",
    field: "studyDescription",
    align: "left",
    style: "width: 300px",
  },
  {
    name: "modalities",
    label: "Modalities",
    field: "modalities",
    align: "left",
    sortable: false,
    style: "width: 120px",
  },
  {
    name: "imageCount",
    label: "#",
    field: "imageCount",
    align: "center",
    sortable: false,
    style: "width: 30px",
  },
  {
    name: "operations",
    label: "",
    field: "operations",
    style: "width: 30px",
  },
];

const seriesColumns = [
  {
    name: "seriesNumber",
    align: "center",
    label: "Series Number",
    field: "seriesNumber",
    sortable: false,
    style: "width: 80px",
  },
  {
    name: "modality",
    align: "center",
    label: "Modality",
    field: "modality",
    sortable: false,
    style: "width: 150px",
  },
  {
    name: "seriesDescription",
    align: "center",
    label: "Series Description",
    field: "seriesDescription",
    sortable: false,
    style: "width: 300px",
  },
  {
    name: "seriesDate",
    align: "center",
    label: "Series Date",
    field: "seriesDate",
    sortable: false,
    style: "width: 150px",
    format: (val) => {
      if (val != "") {
        var day = val.substring(6, 8);
        var month = val.substring(4, 6);
        var year = val.substring(0, 4);
        return `${day}/${month}/${year}`;
      } else {
        return "-";
      }
    },
  },
  {
    name: "instanceCount",
    align: "center",
    label: "Images",
    field: "instanceCount",
    sortable: false,
    style: "width: 80px",
  },
];

export default {
  name: "StudiesTable",
  data() {
    return {
      StudyList: [],
      pagination: {
        sortBy: "name",
        descending: false,
        page: 0,
        rowsPerPage: 0,
      },
      dateDropdownOptions: [
        { text: "Last 7 Days", value: 7 },
        { text: "Last Month", value: 31 },
        { text: "Last Year", value: 365 },
        { text: "All", value: 10000 },
      ],
    };
  },
  setup() {
    const config = require("../config");
    const rows = ref([]);
    const filter = ref({ studyDate: '10000' }); //setting study date to get the default select
    const loading = ref(false);
    const pagination = ref({
      sortBy: "desc",
      descending: false,
      page: 1,
      rowsPerPage: 10,
      rowsNumber: 200,
    });

    var Fetch = new Fetcher();

    async function onRequest(props) {
      const { page, rowsPerPage, sortBy } = props.pagination;

      //Get all filters
      const filter = props.filter;

      // If string search in modalities is not supported
      if (!config.useWebAPI || (config.useWebAPI && !config.webapi.useSQL)) {
        //This is for qido because only works with full Modality names
        if (
          filter.modalities != undefined &&
          filter.modalities != "" &&
          filter.modalities.length != 2
        )
          return; //only search if Modality has 2 chars
      }

      //loading sign
      loading.value = true;

      // Get total studies
      var totalQuery;

      if (config.useWebAPI) {
        totalQuery = `${config.baseUrl}/${config.webapi.totalStudies}?`;
      } else {
        totalQuery = `${config.baseUrl}/${config.qido.root}/studies?`;
      }

      if (filter != undefined) {
        if (filter.studyDate != undefined) {
          //study date
          var today = new Date();
          const filterDate = date.subtractFromDate(today, {
            days: filter.studyDate,
          });
          totalQuery = totalQuery.concat(
            `&00080020=${date.formatDate(
              filterDate,
              "YYYYMMDD"
            )}-${date.formatDate(today, "YYYYMMDD")}`
          );
        }

        if (filter.modalities) {
          totalQuery = totalQuery.concat(`&00080061=${filter.modalities}`);
        }
        if (filter.patientName) {
          totalQuery = totalQuery.concat(`&00100010=*${filter.patientName}*`);
        }
        if (filter.patientID) {
          totalQuery = totalQuery.concat(`&00100020=*${filter.patientID}*`);
        }
        if (filter.studyDescription) {
          totalQuery = totalQuery.concat(
            `&00081030=*${filter.studyDescription}*`
          );
        }
      }

      //patientName,patientID,studyDescription,modalities
      const total = await axios.get(totalQuery).then(function (res) {
        return res.data;
      });

      //console.log(totalQuery);
      // Update local pagination object
      pagination.value.page = page;
      pagination.value.rowsPerPage = rowsPerPage;
      pagination.value.sortBy = sortBy;

      pagination.value.rowsNumber = total;

      var studiesData;

      if (config.useWebAPI) {
        // Refactor filter to method later
        var studiesDataQuery = `${config.baseUrl}/${config.webapi.fetchStudyTable
          }?limit=${config.qido.limit}&offset=${config.qido.offset + (page - 1) * config.qido.limit
          }`;
        if (filter != undefined) {
          if (filter.studyDate != undefined) {
            //study date
            var today = new Date();
            const filterDate = date.subtractFromDate(today, {
              days: filter.studyDate,
            });
            studiesDataQuery = studiesDataQuery.concat(
              `&00080020=${date.formatDate(
                filterDate,
                "YYYYMMDD"
              )}-${date.formatDate(today, "YYYYMMDD")}`
            );
          }

          if (filter.modalities) {
            studiesDataQuery = studiesDataQuery.concat(
              `&00080061=${filter.modalities}`
            );
          }
          if (filter.patientName) {
            studiesDataQuery = studiesDataQuery.concat(
              `&00100010=*${filter.patientName}*`
            );
          }
          if (filter.patientID) {
            studiesDataQuery = studiesDataQuery.concat(
              `&00100020=*${filter.patientID}*`
            );
          }
          if (filter.studyDescription) {
            studiesDataQuery = studiesDataQuery.concat(
              `&00081030=*${filter.studyDescription}*`
            );
          }
        }
        studiesData = await axios.get(studiesDataQuery).then(function (result) {
          return result.data;
        });
      } else {
        // Fetch data from server
        studiesData = await Fetch.FetchStudyTable(
          parseInt(config.qido.limit),
          parseInt(config.qido.offset) +
          (page - 1) * parseInt(config.qido.limit),
          filter
        );
      }

      // Clear existing data, add new
      rows.value = studiesData;

      // Turn off loading indicator
      loading.value = false;
    }

    onMounted(() => {
      // Get initial data from server
      onRequest({
        pagination: pagination.value,
        filter: { studyDate: '10000' }, //starting filter
      });
    });

    return {
      //selected,
      separator: ref("cell"),
      columns,
      seriesColumns,
      rows,
      filter,
      pagination,
      loading,
      fullscreen: ref(false),
      onRequest,
    };
  },
  methods: {
    view(e) {
      let routeData = this.$router.resolve({
        name: "Viewer",
        query: { studyUID: e.row.studyUID },
      });
      window.open(routeData.href);
    },
    viewV3(e) {
      var studyUID = e.row.studyUID;
      var tempURL = `https://dicomwebviewer.medicalconnections.co.uk/?studyUID=${studyUID}`
      window.open(tempURL);
    },
    resetFilters() {
      if (this.filter["patientName"] != undefined) {
        this.filter["patientName"] = "";
      }
      if (this.filter["patientID"] != undefined) {
        this.filter["patientID"] = "";
      }
      if (this.filter["studyDescription"] != undefined) {
        this.filter["studyDescription"] = "";
      }
      if (this.filter["modalities"] != undefined) {
        this.filter["modalities"] = "";
      }
    },
  },
};
</script>


<style>
[dir] .q-table__bottom {
  border-top: 0px solid rgba(0, 0, 0, 0.12);
}

.q-table__top {
  background: #d1d1d1;
  color: #223c69;
}
</style>

<style scoped>
.even-row {}

.odd-row {}

table {
  width: 100%;
  height: 100%;
}

.q-table thead tr,
.q-table tbody td {
  height: 40px !important;
}

[dir] .q-btn {
  padding: 5px !important;
}

.series-info .q-btn {
  width: 100px;
  height: fit-content;
  margin-bottom: auto;
  margin-left: auto;
  margin-right: auto;
}

[dir] .q-table__card {
  background: var(--q-primary);
  color: var(--q-positive) !important;
  margin-top: 30px;
}

.series-info .q-table__card {
  margin-top: 0px;
}

.study-table thead .material-icons {
  color: var(--q-positive) !important;
}

tbody .study-row {
  transition: background-color 0.15s, color 0.15s;
}

tbody .study-row:hover {
  background-color: #c0c0c0;
  color: black;
  cursor: pointer;
}

.info-border {
  border: 1px solid var(--q-info);
}

.series-info {
  min-height: 100px;
}

.expanded-tr {
  height: 130px;
}

.open-in-viewer {
  background: var(--q-positive);
  color: var(--q-secondary);
}

.open-in-viewer .material-icons {
  color: var(--q-secondary) !important;
  font-weight: 900;
  font-size: x-large;
}

.q-btn__content span {
  width: auto;
}

.q-table__bottom .q-table__control {
  color: var(--q-positive) !important;
}

.series-table thead tr {
  background: #d3d0d0c4;
  color: var(--q-positive);
}

.view-button-study {
  color: #d1d1d1;
}

.patient-search {
  margin-left: 32px;
  margin-top: 15px;
}

.search-icon {
  height: 56px;
  color: rgba(0, 0, 0, 0.54);
  font-size: 23px;
}

.study-table thead tr {
  background: var(--q-secondary);
  color: var(--q-positive) !important;
}

.q-field__native {
  color: #d1d1d1 !important;
}

.nosort-header {
  direction: ltr;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.13118px;
  line-height: 20px;
  text-align: left;
  text-decoration-color: var(--q-positive);
  text-decoration-line: none;
  text-decoration-style: solid;
  text-indent: 0px;
  text-overflow: ellipsis;
  text-size-adjust: 100%;
  text-transform: none;
  white-space: nowrap;
  color: var(--q-positive);
}

.q-field--dense .q-field__label {
  margin-left: 11px !important;
}

.q-field--dense .q-field__label {
  font-size: 111px;
  top: 10 px;
}

.study-table thead {
  height: 125px !important;
}

table select {
  width: 200px;
  height: 30px;
  background: var(--q-secondary);
  color: var(--q-positive);
  font-size: 14px;
  font-weight: 400;
  border-right: none;
  border-top: none;
  border-left: none;
  border-bottom: 1px solid;
  border-color: var(--q-positive);
  margin-bottom: 10px;
}

table input {
  color: var(--q-positive) !important;
  font-size: 14px;
  font-weight: 400;
  background: var(--q-secondary);
  min-width: 200px;
  border-right: none;
  border-top: none;
  border-left: none;
  border-bottom: 1px solid;
  border-color: var(--q-positive);
  margin-bottom: 5px;
}

table input::placeholder {
  color: var(--q-positive);
}

#clear-filter span .material-icons {
  color: green !important;
}

table input:focus {
  outline: none;
}

table select:focus {
  outline: none;
}

::placeholder {
  /* Chrome, Firefox, Opera, Safari 10.1+ */
  color: var(--q-positive);
  opacity: 1;
  /* Firefox */
}

:-ms-input-placeholder {
  /* Internet Explorer 10-11 */
  color: var(--q-positive);
}

::-ms-input-placeholder {
  /* Microsoft Edge */
  color: var(--q-positive);
}

.search-icon {
  font-size: 25px;
  -webkit-writing-mode: vertical-lr;
  color: #d7d7d7;
}

.q-tr:hover .view-button-study {
  color: #14284a;
}

.series-info .q-icon {
  padding-left: 0px;
}

.clear-icon {
  font-size: 20px;
  bottom: 1;
  padding-bottom: 2px;
}

#clear-filter:hover {
  background: #0c2144;
}

#clear-filter {
  cursor: pointer;
}

.q-table th.sortable:hover .q-table__sort-icon {
  display: none;
}

.open-buttons {
  display: grid;
  place-self: center;
  padding-left: 25px;
}

.open-buttons button {
  font-size: 13px;
}
.open-buttons span {
  padding-right: 0px;
}

.open-buttons button:first-child {
  margin-bottom: 14px;
  background-color: #4169ab;
  color: #e5dddd;
}

.open-buttons button:nth-child(2) {
  background-color: #3a3cb1;
  color: #e5dddd;
}
</style>
